import { usePersistedState } from "@/lib/persistedFilters";
// Iter 91aj — Humi Home tab. Mirrors the reference screenshot:
//   Greeting + avatar          [Create announcement button]
//   [Inbox] [My tasks] tabs    │  Events sidebar (today/tomorrow/next 7 days)
//   All / Unread filters       │
//   List of items              │
//
// Data:
//   • Inbox items → GET /humi/inbox (reuses notifications collection)
//   • Tasks       → GET /humi/tasks
//   • Events      → GET /humi/events

import { useEffect, useMemo, useState } from "react";
import { Calendar, Cake, Award, PartyPopper, Plane,
         Plus, Star, Loader2, CheckCircle2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Avatar } from "@/components/Avatar";
import { CelebrationsMonthCard } from "@/components/CelebrationsMonthCard";
import { toast } from "sonner";

const EVENT_ICON = { birthday: Cake, anniversary: Award, first_day: PartyPopper, pto: Plane };
const EVENT_LABEL = {
  birthday: (e) => `${e.name}'s birthday`,
  anniversary: (e) => `${e.name} — ${e.years ? `${e.years} yr` : "anniversary"}`,
  first_day: (e) => `${e.name}'s first day`,
  pto: (e) => `${e.name} is away`,
};

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });
}

// Iter 115m — typed due dates auto-format to YYYY-MM-DD as you type.
function maskDate(value) {
  const d = (value || "").replace(/\D/g, "").slice(0, 8);
  if (d.length <= 4) return d;
  if (d.length <= 6) return `${d.slice(0, 4)}-${d.slice(4)}`;
  return `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6)}`;
}

function isValidDate(v) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return false;
  const [y, m, day] = v.split("-").map(Number);
  const d = new Date(Date.UTC(y, m - 1, day));
  return d.getUTCFullYear() === y && d.getUTCMonth() === m - 1 && d.getUTCDate() === day;
}


export default function HumiHome() {
  const { user } = useAuth();
  const [home, setHome] = useState({ greeting: "Hi", name: user?.name || "" });
  const [inbox, setInbox] = useState({ items: [], unread_count: 0 });
  const [tasks, setTasks] = useState({ items: [] });
  const [events, setEvents] = useState({ today: [], tomorrow: [], next_7_days: [] });
  const [inboxFilter, setInboxFilter] = usePersistedState("humi-home.inbox", "all");       // "all" | "unread"
  const [activeTab, setActiveTab] = useState("inbox");         // "inbox" | "tasks"
  const [eventTab, setEventTab] = useState("all");
  const [loading, setLoading] = useState(true);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [taskForm, setTaskForm] = useState({ title: "", description: "", due_date: "" });

  const reload = async () => {
    setLoading(true);
    try {
      const [h, i, t, e] = await Promise.all([
        api.get("/humi/home"), api.get("/humi/inbox"), api.get("/humi/tasks"), api.get("/humi/events"),
      ]);
      setHome(h.data); setInbox(i.data); setTasks(t.data); setEvents(e.data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const inboxItems = useMemo(() => {
    const list = inbox.items || [];
    return inboxFilter === "unread" ? list.filter((x) => !x.read) : list;
  }, [inbox, inboxFilter]);

  const openTasks = useMemo(() => (tasks.items || []).filter((t) => t.status !== "done"), [tasks]);

  const markRead = async (nid) => {
    try {
      await api.post(`/notifications/${nid}/read`);
      setInbox((s) => ({
        items: s.items.map((x) => x.notification_id === nid ? { ...x, read: true } : x),
        unread_count: Math.max(0, s.unread_count - 1),
      }));
    } catch { /* noop */ }
  };

  // Iter 115m — checkbox toggles both ways (was one-way "complete").
  const toggleTask = async (task_id, currentStatus) => {
    const next = currentStatus === "done" ? "open" : "done";
    try {
      await api.patch(`/humi/tasks/${task_id}`, { status: next });
      setTasks((s) => ({ items: s.items.map((t) => t.task_id === task_id ? { ...t, status: next } : t) }));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const createTask = async () => {
    if (!taskForm.title.trim()) { toast.error("Task title is required."); return; }
    if (taskForm.due_date && !isValidDate(taskForm.due_date)) {
      toast.error("Due date must be a real date in YYYY-MM-DD format."); return;
    }
    try {
      await api.post("/humi/tasks", { ...taskForm, title: taskForm.title.trim() });
      toast.success("Task added.");
      setTaskDialogOpen(false); setTaskForm({ title: "", description: "", due_date: "" });
      reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const filteredEvents = useMemo(() => {
    const filter = (arr) => eventTab === "all" ? arr : arr.filter((e) => e.kind === eventTab);
    return {
      today: filter(events.today || []),
      tomorrow: filter(events.tomorrow || []),
      next_7_days: filter(events.next_7_days || []),
    };
  }, [events, eventTab]);

  return (
    <div className="space-y-4" data-testid="humi-home">
      {/* Greeting + create announcement */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Avatar name={user?.name} src={user?.avatar_url} className="h-12 w-12" />
          <div>
            <h1 className="font-heading text-2xl font-semibold text-slate-800 dark:text-slate-100" data-testid="humi-greeting">
              {home.greeting}, {home.name || (user?.name || "").split(" ")[0]}
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">Here&apos;s what&apos;s happening</p>
          </div>
        </div>
        {home.is_hr && (
          <Button data-testid="humi-create-announcement" className="bg-primary hover:bg-primary/90 text-primary-foreground" size="sm">
            <Star className="mr-1.5 h-3.5 w-3.5" /> Create announcement
          </Button>
        )}
      </div>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
        {/* Inbox + Tasks card */}
        <Card className="p-0 border-slate-200 dark:border-slate-700 overflow-hidden">
          <Tabs value={activeTab} onValueChange={setActiveTab} defaultValue="inbox">
            <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 px-4">
              <TabsList className="bg-transparent gap-4">
                <TabsTrigger value="inbox" data-testid="humi-tab-inbox" className="data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none px-1 pb-2">
                  Inbox {inbox.unread_count > 0 && (
                    <span className="ml-2 rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold text-primary" data-testid="humi-inbox-badge">{inbox.unread_count}</span>
                  )}
                </TabsTrigger>
                <TabsTrigger value="tasks" data-testid="humi-tab-tasks" className="data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none px-1 pb-2">
                  My tasks {openTasks.length > 0 && (
                    <span className="ml-2 rounded-full bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-[10px] font-semibold text-slate-600 dark:text-slate-300" data-testid="humi-tasks-badge">{openTasks.length}</span>
                  )}
                </TabsTrigger>
              </TabsList>
              {/* Iter 115n — Filter/more were non-functional placeholders; removed. */}
            </div>

            <TabsContent value="inbox" className="mt-0">
              <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100 dark:border-slate-800">
                <button data-testid="humi-inbox-filter-all"
                        onClick={() => setInboxFilter("all")}
                        className={`rounded px-2 py-0.5 text-xs font-medium ${inboxFilter !== "unread" ? "bg-slate-800 text-white dark:bg-slate-200 dark:text-slate-900" : "text-slate-500 dark:text-slate-400"}`}>All</button>
                <button data-testid="humi-inbox-filter-unread"
                        onClick={() => setInboxFilter("unread")}
                        className={`rounded px-2 py-0.5 text-xs font-medium ${inboxFilter === "unread" ? "bg-slate-800 text-white dark:bg-slate-200 dark:text-slate-900" : "text-slate-500 dark:text-slate-400"}`}>Unread only</button>
                <div className="ml-auto text-xs text-slate-400">{inboxItems.length ? `1-${inboxItems.length} of ${inboxItems.length}` : ""}</div>
              </div>
              <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                {loading && (
                  <li className="flex items-center justify-center p-10 text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /></li>
                )}
                {!loading && inboxItems.length === 0 && (
                  <li className="p-10 text-center text-sm text-slate-400" data-testid="humi-inbox-empty">Nothing new. You&apos;re all caught up.</li>
                )}
                {!loading && inboxItems.map((n) => (
                  <li key={n.notification_id}
                      onClick={() => n.read || markRead(n.notification_id)}
                      data-testid={`humi-inbox-item-${n.notification_id}`}
                      className="flex cursor-pointer items-start gap-3 px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/50">
                    <div className={`mt-1 h-2 w-2 shrink-0 rounded-full ${n.read ? "bg-transparent" : "bg-primary"}`} />
                    <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${n.severity === "warning" ? "bg-amber-100 dark:bg-amber-500/20 text-amber-600 dark:text-amber-300" : n.severity === "critical" ? "bg-red-100 dark:bg-red-500/20 text-red-600 dark:text-red-300" : "bg-emerald-100 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-300"}`}>
                      <Star className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3">
                        <div className={`text-sm ${n.read ? "text-slate-600 dark:text-slate-400" : "font-semibold text-slate-800 dark:text-slate-100"}`}>{n.title}</div>
                        <div className="whitespace-nowrap text-xs text-slate-400">{new Date(n.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</div>
                      </div>
                      {n.body && <div className="mt-0.5 text-xs text-slate-500 dark:text-slate-400 truncate">{n.body}</div>}
                    </div>
                  </li>
                ))}
              </ul>
            </TabsContent>

            <TabsContent value="tasks" className="mt-0">
              <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 px-4 py-3">
                <div className="text-xs text-slate-500 dark:text-slate-400">{openTasks.length} open · {tasks.items.length - openTasks.length} done</div>
                <Button size="sm" variant="outline" data-testid="humi-add-task" onClick={() => setTaskDialogOpen(true)}>
                  <Plus className="mr-1.5 h-3 w-3" /> Add task
                </Button>
              </div>
              <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                {tasks.items.length === 0 && (
                  <li className="p-10 text-center text-sm text-slate-400" data-testid="humi-tasks-empty">No tasks yet. Add one to get started.</li>
                )}
                {tasks.items.map((t) => (
                  <li key={t.task_id} data-testid={`humi-task-item-${t.task_id}`}
                      className="flex items-start gap-3 px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/50">
                    <button
                      onClick={() => toggleTask(t.task_id, t.status)}
                      className="mt-0.5"
                      title={t.status === "done" ? "Mark as not done" : "Mark as done"}
                      data-testid={`humi-task-complete-${t.task_id}`}
                    >
                      {t.status === "done"
                        ? <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                        : <div className="h-5 w-5 rounded-full border-2 border-slate-300 dark:border-slate-600" />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm ${t.status === "done" ? "line-through text-slate-400" : "text-slate-800 dark:text-slate-100"}`}>{t.title}</div>
                      {t.description && <div className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{t.description}</div>}
                      {t.due_date && <div className="mt-1 inline-flex items-center gap-1 rounded bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 text-[10px] text-slate-600 dark:text-slate-300"><Calendar className="h-3 w-3" /> Due {t.due_date}</div>}
                    </div>
                  </li>
                ))}
              </ul>
            </TabsContent>
          </Tabs>
        </Card>

        {/* Events sidebar */}
        <div className="space-y-4">
        <CelebrationsMonthCard />
        <Card className="p-0 border-slate-200 dark:border-slate-700 overflow-hidden">
          <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 px-4 py-3">
            <h3 className="font-heading text-sm font-semibold text-slate-800 dark:text-slate-100">Events</h3>
            <Button variant="ghost" size="icon" className="h-7 w-7" title="Calendar"><Calendar className="h-4 w-4" /></Button>
          </div>
          <div className="flex flex-wrap gap-3 border-b border-slate-100 dark:border-slate-800 px-4 py-2 text-xs">
            {["all", "pto", "birthday", "anniversary", "first_day"].map((k) => (
              <button key={k}
                      data-testid={`humi-events-tab-${k}`}
                      onClick={() => setEventTab(k)}
                      className={`px-1 pb-1 ${eventTab === k ? "border-b-2 border-primary font-semibold text-slate-800 dark:text-slate-100" : "text-slate-500 dark:text-slate-400"}`}>
                {k === "all" ? "All" : k === "pto" ? "Away" : k === "birthday" ? "Birthday" : k === "anniversary" ? "Anniversary" : "First day"}
              </button>
            ))}
          </div>
          <EventsGroup label="Today"    items={filteredEvents.today}    empty="No events today." testid="humi-events-today" />
          <EventsGroup label={fmtDate((events.tomorrow[0]?.date) || new Date(Date.now() + 86400000).toISOString().slice(0,10))}
                       items={filteredEvents.tomorrow}
                       empty="No events tomorrow."
                       testid="humi-events-tomorrow" />
          <EventsGroup label="Next 7 days" items={filteredEvents.next_7_days} empty="Nothing coming up." testid="humi-events-next7" />
        </Card>
        </div>
      </div>

      <Dialog open={taskDialogOpen} onOpenChange={setTaskDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Add a task</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Title</Label>
              <Input data-testid="humi-task-title" value={taskForm.title} onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })} placeholder="e.g. Complete Q3 self-review" />
            </div>
            <div className="space-y-1.5">
              <Label>Description (optional)</Label>
              <Textarea data-testid="humi-task-description" rows={2} value={taskForm.description} onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Due date (optional)</Label>
              <Input data-testid="humi-task-due" inputMode="numeric" maxLength={10}
                placeholder="YYYY-MM-DD" value={taskForm.due_date}
                onChange={(e) => setTaskForm({ ...taskForm, due_date: maskDate(e.target.value) })} />
              {taskForm.due_date && !isValidDate(taskForm.due_date) && (
                <p className="text-[11px] text-red-500" data-testid="humi-task-due-error">Enter a real date as YYYY-MM-DD.</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTaskDialogOpen(false)}>Cancel</Button>
            <Button data-testid="humi-task-save" onClick={createTask} className="bg-primary hover:bg-primary/90 text-primary-foreground">Add task</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EventsGroup({ label, items, empty, testid }) {
  return (
    <div className="border-b border-slate-100 dark:border-slate-800 last:border-b-0 px-4 py-3" data-testid={testid}>
      <div className="mb-2 text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">{label}</div>
      {items.length === 0 ? (
        <div className="text-xs text-slate-400">{empty}</div>
      ) : (
        <ul className="space-y-2">
          {items.map((e, idx) => {
            const Icon = EVENT_ICON[e.kind] || Calendar;
            const label = (EVENT_LABEL[e.kind] || ((x) => x.name))(e);
            return (
              <li key={`${e.kind}-${e.user_id}-${idx}`} className="flex items-center gap-2.5" data-testid={`humi-event-${e.kind}-${e.user_id}`}>
                <Avatar name={e.name} src={e.avatar_url} className="h-8 w-8 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-slate-800 dark:text-slate-100 truncate">{label}</div>
                  <div className="text-[11px] text-slate-500 dark:text-slate-400 flex items-center gap-1">
                    <Icon className="h-3 w-3" />
                    {e.kind === "pto"
                      ? `${e.date} → ${e.end_date}`
                      : new Date(`${e.date}T00:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
